import * as z from "zod/mini";
import { u53Schema } from "./integers";
import { ModeSchema } from "./mode";
import { EpochSchema, RelativeBroadcastSchema } from "./path";

/**
 * Schema for a single binary track: application data published as opaque payloads.
 *
 * The entry says how to read the track, so a consumer needs the track name and nothing else about
 * the application. Prefer a {@link JsonConfig} track when the payloads are JSON, so a generic
 * consumer can read them.
 *
 * A *loose* object: fields this build doesn't recognize pass through untouched, so an entry using
 * a future mode round-trips rather than losing the fields that describe it.
 */
export const BinaryConfigSchema = z.looseObject({
	// Optional reference to another broadcast that publishes this track, expressed
	// relative to the broadcast that served this catalog (e.g. "./source").
	// If unset, the track lives in the same broadcast as the catalog.
	broadcast: z.optional(RelativeBroadcastSchema),
	// The publisher instance the reference names, when known. Absent accepts whichever instance
	// resolves first. An epoch-aware route proving another instance fails the entry; an epochless
	// route cannot prove one, so there it is best effort. Without `broadcast`, it names the
	// catalog's own instance.
	epoch: z.optional(EpochSchema),
	// The track's name in its broadcast, when it differs from this entry's ID (its key in the
	// catalog). Absent means the ID is the track name. Resolve it with `track`.
	track: z.optional(z.string()),

	// Whether the track is a latest-value blob or an append log. Always stated.
	mode: ModeSchema,

	// An optional media type for each payload (e.g. "image/jpeg"). Purely descriptive:
	// a consumer that doesn't recognize it can still read the track.
	mime: z.optional(z.string()),

	// The maximum bitrate of the track in bits per second, if known.
	bitrate: z.optional(u53Schema),

	// The maximum delay between a payload being ready and the publisher flushing it, in whole
	// milliseconds rounded up, with the same meaning as a video rendition's `jitter`.
	jitter: z.optional(
		z.pipe(
			u53Schema,
			z.transform((value) => (value === 0 ? undefined : value)),
		),
	),

	// How far this track's payloads reach the transport behind the broadcast's earliest rendition,
	// with the same meaning and encoding as a video rendition's `delay`. Only measured for payloads
	// that carry a capture time.
	delay: z.optional(
		z.pipe(
			u53Schema,
			z.transform((value) => (value === 0 ? undefined : value)),
		),
	),
});

/**
 * Schema for the catalog `binary` section: a map of track name to config.
 *
 * Not a rendition set: entries are distinct tracks, not alternatives to choose between. The map
 * key is the track name to subscribe to.
 */
export const BinarySchema = z.object({
	tracks: z.record(z.string(), BinaryConfigSchema),
});

/** The catalog binary section: data tracks keyed by track name. */
export type Binary = z.infer<typeof BinarySchema>;
/** How to read one binary track. */
export type BinaryConfig = z.infer<typeof BinaryConfigSchema>;
